import React, { useState } from 'react'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import { type KycArchiveViewProps } from './KycArchiveApplicationPage'
import {
  useGetKycArchiveDocumentsQuery,
  useGetKycArchiveDocumentUrlLazyQuery
} from '../../../api-services/kyc-archive/kycArchiveApi'
import {
  type AcuantDocumentImageDto,
  type AcuantSignedUrlDto
} from '../../../api-schema-types/kyc-archive/KycArchiveDtos'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { useAppDispatch } from '../../../app/hooks'
import { openInNewTab } from '../../../utility/dom/DomWindowHelpers'
import { KycArchiveAttachmentButton } from './KycArchiveAttachmentButton'

interface ImageDialogState {
  open: boolean
  title: string
  s3Url: string | null
}

const formatFileSizeKb = (fileSize: number | null): string => {
  if (fileSize === null) return '-'
  return `${Math.round(fileSize / 1024)} KB`
}

const KycArchiveDocumentsView: React.FC<KycArchiveViewProps> = ({
  applicationId
}: KycArchiveViewProps) => {
  const dispatch = useAppDispatch()

  const [imageDialog, setImageDialog] = useState<ImageDialogState>({ open: false, title: '', s3Url: null })
  const [apiGetDocumentUrlTrigger] = useGetKycArchiveDocumentUrlLazyQuery()

  const getDocumentsApiResult = useGetKycArchiveDocumentsQuery(applicationId)

  const handleViewImage = async (image: AcuantDocumentImageDto): Promise<void> => {
    apiGetDocumentUrlTrigger(image.id)
      .unwrap()
      .then((triggerResult) => {
        const url = (triggerResult as AcuantSignedUrlDto).s3Url
        setImageDialog({
          open: true,
          title: `Document ${image.secondaryId ?? '-'} (${image.side ?? '-'})`,
          s3Url: url
        })
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const handleCloseImageDialog = (): void => {
    setImageDialog({ open: false, title: '', s3Url: null })
  }

  const groupImagesBySecondaryId = (images: AcuantDocumentImageDto[]): Map<string, AcuantDocumentImageDto[]> => {
    const groups = new Map<string, AcuantDocumentImageDto[]>()
    images.forEach(image => {
      const key = image.secondaryId ?? 'Unknown document'
      const group = groups.get(key) ?? []
      group.push(image)
      groups.set(key, group)
    })
    return groups
  }

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>Document Images</h3>
        {getDocumentsApiResult.isLoading && <LoadingComponent/>}
        {(getDocumentsApiResult.error != null) &&
          <ErrorComponent message={RtkQueryHelper.mapResponseError(getDocumentsApiResult.error)}/>}
        {getDocumentsApiResult.isSuccess && getDocumentsApiResult.data.images.length === 0 && (
          <Typography variant={'body1'}>No document images archived for this application.</Typography>
        )}
        {getDocumentsApiResult.isSuccess &&
          Array.from(groupImagesBySecondaryId(getDocumentsApiResult.data.images).entries()).map(([secondaryId, images]) => (
            <Box key={secondaryId} sx={{ mb: 3 }}>
              <Typography variant={'subtitle1'} sx={{ mb: 2 }}>Document: {secondaryId}</Typography>
              <Grid container spacing={3}>
                {images.map(image => (
                  <Grid item key={image.id} lg={3} md={4} sm={6}>
                    <Card variant={'outlined'}>
                      <CardContent>
                        <Typography variant={'h6'}>{image.side ?? '-'}</Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                          {formatFileSizeKb(image.fileSize)}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          variant={'outlined'}
                          size={'small'}
                          startIcon={<ImageOutlinedIcon/>}
                          onClick={async () => { await handleViewImage(image) }}
                        >
                          View
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
      </Box>

      <Box sx={{ pb: 4 }}>
        <h3>Attachments</h3>
        {getDocumentsApiResult.isSuccess && getDocumentsApiResult.data.attachments.length === 0 && (
          <Typography variant={'body1'}>No attachments archived for this application.</Typography>
        )}
        {getDocumentsApiResult.isSuccess && getDocumentsApiResult.data.attachments.map(attachment => (
          <Box key={attachment.id} sx={{ display: 'inline-block', mr: 2, mb: 1 }}>
            <KycArchiveAttachmentButton attachment={attachment}/>
          </Box>
        ))}
      </Box>

      <Dialog open={imageDialog.open} onClose={handleCloseImageDialog} maxWidth={'lg'} fullWidth>
        <DialogTitle>{imageDialog.title}</DialogTitle>
        <DialogContent>
          {imageDialog.s3Url === '' && (
            <Typography variant={'body1'}>Document images are not accessible from this environment</Typography>
          )}
          {imageDialog.s3Url !== null && imageDialog.s3Url !== '' && (
            <img src={imageDialog.s3Url} alt={imageDialog.title} style={{ maxWidth: '100%' }}/>
          )}
        </DialogContent>
        <DialogActions>
          {imageDialog.s3Url !== null && imageDialog.s3Url !== '' && (
            <Button onClick={() => { openInNewTab(imageDialog.s3Url ?? '') }}>Open in new tab</Button>
          )}
          <Button onClick={handleCloseImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================================================================== */}
    </>
  )
}

export default KycArchiveDocumentsView
